/**
 * RG-PA-01 / RG-PA-02 / RG-PA-03 — Éligibilité d'un patient aux soins.
 *
 * Référence : CCT-2026-AFS-MOD01 § 3.1.1
 *
 * ── Modèle de référence pour toutes les règles métier ──────────────────
 * Une règle vit ici, dans `packages/shared/rules`, quand elle est une
 * décision pure. Aucune I/O, aucun Prisma, aucune horloge implicite : le
 * service charge les données, appelle la règle, agit sur le résultat.
 *
 * Bénéfices concrets :
 *   · testable sans base de données
 *   · le front peut afficher la même décision, avec le même motif
 *   · quand la règle change, un seul endroit change
 */
/**
 * Reprend valeur pour valeur l'enum `StatutAdherent` du schéma Prisma.
 * Les deux doivent rester alignées : un écart ferait diverger ce que la
 * base autorise de ce que cette règle décide, sans que rien ne le
 * signale. `A_JOUR` s'appelait ainsi avant que le référentiel existe ;
 * c'est `ACTIF` désormais, comme en base.
 *
 * Une liste et non une union nue : le schéma de réponse de l'API doit
 * pouvoir l'énumérer, et une union TypeScript ne s'énumère pas à
 * l'exécution.
 */
export const STATUTS_ADHERENT = [
    'ACTIF',
    'EN_RETARD',
    'SUSPENDU',
    'RESILIATION_EN_PREAVIS',
    'RESILIE',
];
export const MOTIFS_NON_ELIGIBLE = [
    'ADHERENT_SUSPENDU',
    'ADHERENT_RESILIE',
    'ABSENT_DU_DNS',
    'TITULAIRE_NON_ELIGIBLE',
    /**
     * Décision du Tech Lead, 18/09/2026 : une fiche créée à l'accueil n'ouvre
     * la prise en charge qu'APRÈS sa confirmation par la chargée de clientèle,
     * prévenue en temps réel. Remplace « servi d'abord, confirmé ensuite ».
     */
    'EN_ATTENTE_DE_CONFIRMATION',
    /** RG-PA-02 — pas un refus de soins : une orientation vers le paiement immédiat. */
    'CLIENT_EXTERNE',
    /** Anomalie de données : sans contrat, aucune entreprise dont vérifier le statut. */
    'CONTRAT_INCONNU',
    /**
     * Décision du Tech Lead, 29/09/2026 : la chargée de clientèle a retiré ce
     * conjoint ou cet enfant de la couverture de son salarié. « Absent du DNS »
     * serait faux : un DNS ne nomme jamais un ayant droit (CP-01).
     */
    'AYANT_DROIT_RETIRE',
    /**
     * Réunion du 15/10 : la chargée de clientèle a déclaré ce salarié parti,
     * sur courrier de son entreprise. « Absent du DNS » serait faux : il y
     * figure peut-être encore.
     */
    'SALARIE_PARTI',
    /**
     * Réunion du 15/10 : un enfant est couvert jusqu'au jour anniversaire de
     * la limite de son entreprise inclus (21 ans par défaut, décision 338),
     * plus à partir du lendemain.
     */
    'ENFANT_AU_DELA_DE_L_AGE',
];
/**
 * Réunion du 15/10 — l'âge jusqu'auquel un enfant est couvert au titre de son
 * parent, par défaut. Décision 338 : chaque entreprise a le sien
 * (`Adherent.ageLimiteEnfant`, BRED 25), borné par `AGE_LIMITE_ENFANT_MIN/MAX`.
 */
export const AGE_LIMITE_ENFANT = 21;
export const AGE_LIMITE_ENFANT_MIN = 18;
export const AGE_LIMITE_ENFANT_MAX = 30;
const MESSAGES = {
    ADHERENT_SUSPENDU: "L'entreprise adhérente est suspendue. Prise en charge impossible.",
    ADHERENT_RESILIE: "L'adhésion de cette entreprise a pris fin. Prise en charge impossible.",
    // Couvre aussi l'ayant droit désactivé : il n'a jamais figuré au DNS,
    // mais il n'est plus sur la liste active non plus (RG-PA-04).
    ABSENT_DU_DNS: "Le bénéficiaire n'est plus sur la liste active de son entreprise : absent de la " +
        'dernière déclaration DNS, ou désactivé.',
    TITULAIRE_NON_ELIGIBLE: "Le titulaire de ce bénéficiaire n'est pas éligible.",
    EN_ATTENTE_DE_CONFIRMATION: "Fiche enregistrée à l'accueil : le patient patiente jusqu'à la confirmation de la " +
        'chargée de clientèle, qui a été prévenue. L’accueil est notifié dès qu’elle confirme.',
    CLIENT_EXTERNE: 'Client externe : consultation possible avec paiement immédiat, au tarif externe.',
    CONTRAT_INCONNU: "Ce dossier n'est rattaché à aucun contrat. À faire compléter par le chargé de clientèle.",
    AYANT_DROIT_RETIRE: 'Ayant droit retiré de la couverture de son salarié par la chargée de clientèle. ' +
        'Paiement immédiat possible.',
    SALARIE_PARTI: "Salarié déclaré parti par la chargée de clientèle, sur courrier de l'entreprise. " +
        'Paiement immédiat possible.',
    ENFANT_AU_DELA_DE_L_AGE: messageEnfantAuDelaDeLAge(AGE_LIMITE_ENFANT),
};
/**
 * Décision 338 — le message dit la limite de l'entreprise du titulaire
 * (21 ans par défaut, 25 pour BRED).
 */
export function messageEnfantAuDelaDeLAge(ageLimite) {
    return (`Enfant de plus de ${String(ageLimite)} ans : il n’est plus couvert au titre de son parent ` +
        'depuis le lendemain de son anniversaire. Paiement immédiat possible.');
}
/**
 * Le motif en deux mots, pour une colonne de tableau.
 *
 * `MESSAGES` porte la phrase complète, qui explique et oriente ; celle-ci ne
 * tient pas dans une cellule. Ce libellé court la résume sans la remplacer :
 * l'écran affiche les deux, le libellé dans la colonne et la phrase au
 * survol et dans le détail.
 *
 * Ici et non dans le front, comme `LIBELLE_ROLE` : ces libellés partiront
 * aussi dans les exports Direction et dans les PDF produits par l'API.
 */
export const LIBELLE_MOTIF_NON_ELIGIBLE = {
    ADHERENT_SUSPENDU: 'Entreprise suspendue',
    ADHERENT_RESILIE: 'Adhésion terminée',
    ABSENT_DU_DNS: 'Absent du dernier DNS',
    TITULAIRE_NON_ELIGIBLE: 'Titulaire non éligible',
    EN_ATTENTE_DE_CONFIRMATION: 'En attente de confirmation',
    CLIENT_EXTERNE: 'Client externe',
    CONTRAT_INCONNU: 'Contrat inconnu',
    AYANT_DROIT_RETIRE: 'Ayant droit retiré',
    SALARIE_PARTI: 'Salarié parti',
    // Décision 338 : la limite dépend de l'entreprise ; la phrase complète la dit.
    ENFANT_AU_DELA_DE_L_AGE: 'Enfant au-delà de l’âge',
};
/**
 * RG-PA-01b, lue seule : le statut de l'entreprise laisse-t-il passer les
 * soins ? La fiche patient affiche les deux conditions séparément, pour que
 * l'agent voie sur laquelle la décision repose et puisse l'expliquer au
 * patient. `evaluerEligibilite()` reste la seule à rendre le verdict.
 */
export function entrepriseCouvre(statut) {
    return statut !== 'SUSPENDU' && statut !== 'RESILIE';
}
function refus(motif) {
    // Une décision qui refuse un droit expose TOUJOURS son motif : un « Refusé »
    // sans raison est inexploitable par l'agent d'accueil (règle transverse).
    return { eligible: false, motif, message: MESSAGES[motif] };
}
/**
 * RG-PA-01 — Un patient est éligible SI ET SEULEMENT SI :
 *   (a) il figure dans le dernier DNS importé de son entreprise, ET
 *   (b) l'entreprise n'est pas suspendue.
 *
 * Les deux conditions sont conjonctives. Ne jamais les relâcher en « OU ».
 *
 * Trois des cinq statuts d'adhérent laissent passer :
 *
 *   ACTIF                   couvert
 *   EN_RETARD               couvert — l'impayé se recouvre, il ne se soigne
 *                           pas au guichet ; la suspension n'est jamais
 *                           automatique (RG-FIN-08)
 *   RESILIATION_EN_PREAVIS  couvert — CP-05, trois mois pendant lesquels
 *                           les soins continuent
 *   SUSPENDU                bloque
 *   RESILIE                 bloque
 *
 * `RESILIE` bloque par déduction et non par une règle écrite : le CCT ne
 * traite pas l'après-préavis. Servir des soins au titre d'une adhésion
 * éteinte créerait une créance sur une entreprise qui n'est plus liée à
 * AFS. À confirmer avec la Direction ; en attendant, on ferme.
 */
export function evaluerEligibilite(ctx) {
    // RG-PA-03 — L'ayant droit hérite de l'éligibilité du titulaire.
    if (ctx.eligibiliteTitulaire !== null && !ctx.eligibiliteTitulaire.eligible) {
        return refus('TITULAIRE_NON_ELIGIBLE');
    }
    // RG-PA-01b
    if (ctx.statutAdherent === 'SUSPENDU') {
        return refus('ADHERENT_SUSPENDU');
    }
    // CP-05 — après le préavis, l'adhésion n'existe plus.
    if (ctx.statutAdherent === 'RESILIE') {
        return refus('ADHERENT_RESILIE');
    }
    // Après l'entreprise, avant le DNS : sous une entreprise suspendue, c'est
    // la suspension qu'il faut lire — la confirmation n'y changerait rien.
    if (ctx.enAttenteDeConfirmation) {
        return refus('EN_ATTENTE_DE_CONFIRMATION');
    }
    // Décision du 29/09 — retiré par la chargée : c'est ce motif qu'il faut
    // lire, pas une absence du DNS où il n'a jamais figuré.
    if (ctx.retireDeLaCouverture) {
        return refus('AYANT_DROIT_RETIRE');
    }
    // Réunion du 15/10 — déclaré parti par la chargée : c'est ce qu'il faut
    // lire, pas une absence du DNS.
    if (ctx.partiALaMain === true) {
        return refus('SALARIE_PARTI');
    }
    // Réunion du 15/10 — l'âge passe avant le DNS : un enfant de 22 ans n'est
    // plus couvert, même si rien d'autre ne l'arrête.
    if (ctx.enfantAuDelaDeLAge === true) {
        return {
            eligible: false,
            motif: 'ENFANT_AU_DELA_DE_L_AGE',
            message: messageEnfantAuDelaDeLAge(ctx.ageLimiteEnfant ?? AGE_LIMITE_ENFANT),
        };
    }
    // RG-PA-01a — un ayant droit n'a pas à figurer au DNS : celui-ci ne
    // déclare que des compteurs (CP-01).
    if (!ctx.presentDansDernierDns && !ctx.dispenseDeVerificationDns) {
        return refus('ABSENT_DU_DNS');
    }
    return { eligible: true };
}
/**
 * RG-PA-02 — Un client externe n'a ni entreprise ni DNS : il n'est jamais
 * « éligible » au sens de RG-PA-01, et c'est normal. Ce résultat porte le
 * motif qui l'oriente vers le paiement immédiat — pas un refus de soins.
 */
export function eligibiliteClientExterne() {
    return refus('CLIENT_EXTERNE');
}
/**
 * Un titulaire sans contrat est une anomalie de données : l'import et
 * l'enrôlement en posent toujours un. Fermé par défaut — on ne devine pas
 * une entreprise — mais avec un motif qui dit quoi faire, et le parcours
 * client externe reste ouvert (RG-PA-02).
 */
export function eligibiliteSansContrat() {
    return refus('CONTRAT_INCONNU');
}
/**
 * RG-PA-02 — Un patient non couvert consulte quand même, avec paiement
 * immédiat au tarif externe : le client externe, mais aussi le salarié dont
 * l'entreprise est suspendue. C'est le PARCOURS de la visite — à ne pas
 * confondre avec le TYPE « client externe » d'une personne
 * (`rules/beneficiaire.ts`).
 *
 * Non éligible ≠ refusé. Ne jamais bloquer purement et simplement le parcours
 * à l'accueil : c'est une orientation, pas une porte fermée.
 */
export const PARCOURS_POSSIBLES = ['PRIS_EN_CHARGE', 'PAIEMENT_IMMEDIAT'];
export function determinerParcours(resultat) {
    return resultat.eligible ? 'PRIS_EN_CHARGE' : 'PAIEMENT_IMMEDIAT';
}
/**
 * Un enfant est couvert jusqu'au jour anniversaire de `ageLimite` INCLUS,
 * plus à partir du lendemain. `dateNaissance` et `aujourdHui` au format
 * AAAA-MM-JJ. Sans date de naissance, on ne sait pas : on ne coupe pas —
 * l'enfant n'est jamais lu « non couvert » pour autant ; c'est son entrée qui
 * attend la date (voir `dateNaissanceASaisir`). Un enfant né un 29 février a
 * ses 21 ans le 1er mars d'une année non bissextile.
 */
export function enfantAuDelaDeLAge(dateNaissance, aujourdHui, ageLimite = AGE_LIMITE_ENFANT) {
    if (dateNaissance === null)
        return false;
    const [annee, mois, jour] = dateNaissance.split('-').map(Number);
    if (annee === undefined || mois === undefined || jour === undefined)
        return false;
    const anniversaire = new Date(Date.UTC(annee + ageLimite, mois - 1, jour));
    return aujourdHui > anniversaire.toISOString().slice(0, 10);
}
/**
 * Décision 8 du TL (16/10) — un enfant sans date de naissance : refus
 * « léger ». Son entrée en file attend la date, que l'accueil saisit sur
 * place ; l'entrée se fait aussitôt après. Il n'est jamais affiché comme
 * inactif ni « non couvert » : l'écran dit « date de naissance à saisir ».
 */
export function dateNaissanceASaisir(personne) {
    return personne.typeBeneficiaire === 'ENFANT' && personne.dateNaissance === null;
}
/** Ce que l'accueil lit quand l'entrée d'un enfant attend sa date de naissance. */
export const MESSAGE_DATE_NAISSANCE_A_SAISIR = 'Date de naissance à saisir : un enfant est couvert jusqu’à l’âge limite de son entreprise (21 ans sauf accord). ' +
    'Saisissez-la sur la fiche, puis notez l’entrée — aucune attente.';
/**
 * Le verdict d'un jour donné (`aujourdHui` au format AAAA-MM-JJ, date civile
 * de Madagascar), à partir des conditions : le même calcul en ligne et dans
 * le coffre hors ligne.
 */
export function eligibiliteAuJour(conditions, personne, aujourdHui) {
    const { titulaireEligible, ...reste } = conditions;
    return evaluerEligibilite({
        ...reste,
        // Seul `eligible` du titulaire est lu : le motif rendu est toujours
        // TITULAIRE_NON_ELIGIBLE, quel que soit le sien.
        eligibiliteTitulaire: titulaireEligible === null
            ? null
            : titulaireEligible
                ? { eligible: true }
                : refus('TITULAIRE_NON_ELIGIBLE'),
        enfantAuDelaDeLAge: personne.typeBeneficiaire === 'ENFANT' &&
            enfantAuDelaDeLAge(personne.dateNaissance, aujourdHui, conditions.ageLimiteEnfant),
    });
}
//# sourceMappingURL=eligibilite.js.map